import PropTypes from 'prop-types';
import withRouter from '@utils/wrappers/WithRouter';
import WithAllPropsApp from '@utils/wrappers/WithAllPropsApp';
import React, { Component, Fragment } from 'react';
import { withStyles } from '@mui/styles';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListSubheader from '@mui/material/ListSubheader';
import { Grid, Typography } from '@ts/storybook';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Immutable from 'immutable';
import Misc from '@components/Misc';
import { QueryPaginatedSelection } from '@components/Inputs';
import NeedsManager from '@utils/NeedsManager';
import EntityFilter from './EntityFilter';
import Error403 from '@components/Errors/403';
import App from '@gral/app';
import { withTranslation } from 'react-i18next';

const styles = theme => ({
  root: {
    width: '100%',
  },
  listLong: {
    overflow: 'auto',
    maxHeight: '650px',
  },
  paper: {
    padding: '16px',
  },
});

class RolePrivileges extends Component {
  state = {
    selectedEntity: null,
    selectedContext: null,
    selectedSubcontext: null,
    selectedAction: null,
    selectedFilter: null,
  };

  constructor(props) {
    super(props);

    const needs = [
      {
        url: '/api/entities',
        dataPath: 'entities',
        data: { context: { context: 'role.acls', subcontext: 'all' } },
      },
    ];
    this.needsManager = new NeedsManager(this.props.data, needs);
    this.needsManager.fetchNeeds();
    this.checkPermission();
  }

  checkPermission() {
    const { app } = this.props;
    const modeActionMap = {
      roleView: 'read',
      roleEdit: 'edit',
      roleNew: 'create',
    };
    const action = modeActionMap[this.props.transferPropsRoute.id];

    if (!App.cxStore.checkPermission('taAclPrivileges', action)) {
      this._NOT_AUTHORIZED = this.props.t('This action is not allowed for this role for this entity.');
    }
  }

  _renderError(reason) {
    return <Error403 details={reason} />;
  }

  handleListItemClick = type => (event, entity) => {
    switch (type) {
      case 'entity':
        this.setState({
          selectedEntity: entity,
          selectedContext: null,
          selectedSubcontext: null,
          selectedAction: null,
          selectedFilter: null,
        });
        break;
      case 'context':
        this.setState({
          selectedContext: !!this.state.selectedEntity && entity,
          selectedSubcontext: null,
          selectedAction: null,
          selectedFilter: null,
        });
        break;
      case 'subcontext':
        this.setState({
          selectedSubcontext: !!this.state.selectedContext && entity,
          selectedAction: null,
          selectedFilter: null,
        });
        break;
      case 'action':
        this.setState({
          selectedAction: !!this.state.selectedSubcontext && entity,
          selectedFilter: this.state.selectedSubcontext.get('filters')
            ? this.state.selectedSubcontext.getIn(['filters', 0, 'as'])
            : null,
        });
        break;
      default:
        break;
    }
  };
  //-----------------------------------------------------
  render() {
    const { classes } = this.props;
    this._curValues = this.props.values;

    if (!Immutable.isImmutable(this._curValues.aclPrivileges)) {
      this._curValues.aclPrivileges = Immutable.fromJS(this._curValues.aclPrivileges || []);
    }
    this._fDisabled = this.props.mode === 'view';

    this._entities = this.props.data && this.props.data.getIn(['entities', 'data']);
    if (this._NOT_AUTHORIZED) {
      return this._renderError(this._NOT_AUTHORIZED);
    }
    if (this._entities) {
      this._entities = this._entities.sortBy(f => f.get('name'));
    } else {
      return <Misc.Loading />;
    }

    return (
      <>
        <Grid container spacing={2} justify="center" alignItems="flex-start" className={classes.root}>
          <Grid size={{ sm: 6, lg: 2 }}>{this._renderEntities()}</Grid>
          <Grid size={{ sm: 6, lg: 1 }}>{this._renderConxtexts()}</Grid>
          <Grid size={{ sm: 6, lg: 1 }}>{this._renderSubcontexts()}</Grid>
          <Grid size={{ sm: 6, lg: 1 }}>{this._renderActions()}</Grid>
          <Grid size={{ sm: 6, lg: 7 }}>
            {this.state.selectedAction !== null && (
              <>
                {this._renderEntityFilter()}
                <br />
                {this._renderConditions()}
              </>
            )}
          </Grid>
        </Grid>
      </>
    );
  }

  _renderEntities() {
    const { classes } = this.props;
    return (
      <List
        className={classes.listLong}
        component="nav"
        aria-labelledby="entities-list-subheader"
        subheader={
          <ListSubheader disableSticky component="div" id="entities-list-subheader">
            Target entity
          </ListSubheader>
        }
      >
        {this._entities &&
          this._entities.map((entity, index) => {
            const renderBold = this._isAclAlive(entity.get('logicalId'), null, null, null);
            return (
              <ListItem
                button
                key={entity.get('logicalId')}
                selected={this.state.selectedEntity && this.state.selectedEntity.get('logicalId') === entity.get('logicalId')}
                onClick={event => this.handleListItemClick('entity')(event, entity)}
              >
                {!renderBold && entity.get('name')}
                {renderBold && <Box fontWeight="fontWeightMedium">{entity.get('name')}</Box>}
              </ListItem>
            );
          })}
      </List>
    );
  }

  _renderConxtexts() {
    return (
      <List
        component="nav"
        key={`contexts_${this.state.selectedEntity && this.state.selectedEntity.get('logicalId')}`}
        aria-labelledby="contexts-list-subheader"
        subheader={
          <ListSubheader component="div" id="contexts-list-subheader">
            Context
          </ListSubheader>
        }
      >
        {this.state.selectedEntity &&
          this.state.selectedEntity.get('contexts') &&
          this.state.selectedEntity
            .get('contexts')
            .sortBy(f => f.getIn(['name']))
            .map((context, index) => {
              const renderBold = this._isAclAlive(
                this.state.selectedEntity.get('logicalId'),
                context.getIn(['logicalId']),
                null,
                null
              );
              return (
                <ListItem
                  button
                  key={context.getIn(['logicalId'])}
                  selected={
                    this.state.selectedContext && this.state.selectedContext.getIn(['logicalId']) === context.getIn(['logicalId'])
                  }
                  onClick={event => this.handleListItemClick('context')(event, context)}
                >
                  {!renderBold && context.getIn(['name'])}
                  {renderBold && <Box fontWeight="fontWeightMedium">{context.getIn(['name'])}</Box>}
                </ListItem>
              );
            })}
      </List>
    );
  }

  _renderSubcontexts() {
    return (
      <List
        component="nav"
        key={`subcontexts_${this.state.selectedContext && this.state.selectedContext.getIn(['logicalId'])}`}
        aria-labelledby="subcontexts-list-subheader"
        subheader={
          <ListSubheader component="div" id="subcontexts-list-subheader">
            Subcontext
          </ListSubheader>
        }
      >
        {this.state.selectedContext &&
          this.state.selectedContext
            .get('subcontexts')
            .sortBy(f => f.getIn(['name']))
            .map((subcontext, index) => {
              const renderBold = this._isAclAlive(
                this.state.selectedEntity.get('logicalId'),
                this.state.selectedContext.get('logicalId'),
                subcontext.getIn(['logicalId']),
                null
              );
              return (
                <ListItem
                  button
                  key={subcontext.getIn(['logicalId'])}
                  selected={
                    this.state.selectedSubcontext &&
                    this.state.selectedSubcontext.getIn(['logicalId']) === subcontext.getIn(['logicalId'])
                  }
                  onClick={event => this.handleListItemClick('subcontext')(event, subcontext)}
                >
                  {!renderBold && subcontext.getIn(['name'])}
                  {renderBold && <Box fontWeight="fontWeightMedium">{subcontext.getIn(['name'])}</Box>}
                </ListItem>
              );
            })}
      </List>
    );
  }

  _renderActions() {
    return (
      <List
        component="nav"
        key={`actions_${this.state.selectedSubcontext && this.state.selectedSubcontext.get('logicalId')}`}
        aria-labelledby="actions-list-subheader"
        subheader={
          <ListSubheader component="div" id="actions-list-subheader">
            Action
          </ListSubheader>
        }
      >
        {this.state.selectedSubcontext &&
          this.state.selectedSubcontext
            .get('actions')
            .sortBy(f => f.get('name'))
            .map((action, index) => {
              const renderBold = this._isAclAlive(
                this.state.selectedEntity.get('logicalId'),
                this.state.selectedContext.getIn(['logicalId']),
                this.state.selectedSubcontext.getIn(['logicalId']),
                action.getIn(['logicalId'])
              );
              return (
                <ListItem
                  button
                  key={action.getIn(['logicalId'])}
                  selected={
                    this.state.selectedAction && this.state.selectedAction.get('logicalId') === action.getIn(['logicalId'])
                  }
                  onClick={event => this.handleListItemClick('action')(event, action)}
                >
                  {!renderBold && action.getIn(['name'])}
                  {renderBold && <Box fontWeight="fontWeightMedium">{action.getIn(['name'])}</Box>}
                </ListItem>
              );
            })}
      </List>
    );
  }

  _renderEntityFilter() {
    const { classes } = this.props;
    const entity = this.state.selectedEntity.get('logicalId');
    const context = this.state.selectedContext.getIn(['logicalId']);
    const subcontext = this.state.selectedSubcontext.getIn(['logicalId']);
    const action = this.state.selectedAction.get('logicalId');

    const acl = this._getAclSet(entity, context, subcontext, action);

    return (
      <Paper className={classes.paper}>
        <Typography variant="subtitle1" text={this.state.selectedEntity.get('name')} />
        <br />
        <EntityFilter
          app={this.props.app}
          data={this.props.data}
          t={this.props.t}
          mode={this.props.mode}
          entityId={this.state.selectedEntity.get('logicalId')}
          filters={this.state.selectedSubcontext.get('filters')}
          allowIndividualInclusion={this.state.selectedAction.getIn(['data', 'allowIndividualInclusion'])}
          allowIndividualExclusion={this.state.selectedAction.getIn(['data', 'allowIndividualExclusion'])}
          disabled={this._fDisabled}
          enabled={acl && acl.getIn(['isAlive'])}
          includeAll={acl && acl.getIn(['data', 'includeAll'])}
          includeEntities={acl && acl.getIn(['data', 'includeEntities'])}
          excludeEntities={acl && acl.getIn(['data', 'excludeEntities'])}
          filterEntities={acl && acl.getIn(['data', 'filterEntities'])}
          filterType={acl && acl.getIn(['data', 'filterType'])}
          onChange={this._onChangeAclValue(entity, context, subcontext, action)}
        />
      </Paper>
    );
  }

  _renderConditions() {
    const { classes } = this.props;
    const acl = this._getAclSet(
      this.state.selectedEntity.get('logicalId'),
      this.state.selectedContext.getIn(['logicalId']),
      this.state.selectedSubcontext.getIn(['logicalId']),
      this.state.selectedAction.get('logicalId')
    );

    return (
      <Paper className={classes.paper}>
        <Typography variant="subtitle1" text="Subcontext conditions" />
        <br />
        {!this.state.selectedSubcontext.getIn(['data', 'conditions']) && (
          <Typography variant="subtitle2" text="This subcontext does not have configurable conditions for this action" />
        )}
        {this.state.selectedSubcontext.getIn(['data', 'conditions']) &&
          this.state.selectedSubcontext.getIn(['data', 'conditions']).map((condition, index) => {
            const includeAllCondition = (acl && acl.getIn(['contextData', 'conditions', index, 'includeAll'])) || false;
            const includeEntities = includeAllCondition
              ? []
              : (acl && acl.getIn(['contextData', 'conditions', index, 'includeEntities'])) || [];
            return (
              <Fragment
                key={`${condition.getIn(['entityId'])}_${this.state.selectedAction.get('logicalId')}_${condition.getIn(['as']) || condition.getIn(['entityId'])
                  }`}
              >
                <QueryPaginatedSelection
                  app={this.props.app}
                  data={this.props.data}
                  title={`${this.props.t('With ')} ${condition.getIn(['name']) ||
                    this._entities.find(e => e.get('logicalId') === condition.getIn(['entityId'])).get('name')
                    }`}
                  upperExtraComponent={
                    <FormControlLabel
                      control={
                        <Switch
                          checked={includeAllCondition}
                          onChange={this._onEnableConditionEvent(index)}
                          value="enableCondition"
                          color="primary"
                          disabled={this._fDisabled || (acl ? acl.get('isAlive') === false : true)}
                        />
                      }
                      label={includeAllCondition ? this.props.t('Any') : this.props.t('Only these')}
                    />
                  }
                  selectedIds={includeEntities}
                  columns={this._entityColumns()}
                  pkColumn={'id'}
                  itemsFetchUrl={'/api/entitiesGeneric'}
                  fnChipCaption={item => item.get('name')}
                  onChangeSelected={this._onChangeContextConditionEntities(index, 'includeEntities')}
                  disabled={this._fDisabled || includeAllCondition || (acl ? acl.get('isAlive') === false : true)}
                  fetchData={{ entityId: condition.getIn(['entityId']) }}
                  columnToRetrieve={'logicalId'}
                />
              </Fragment>
            );
          })}
      </Paper>
    );
  }

  _entityColumns() {
    return [
      {
        fnLabel: () => `${this.props.t('ID')}`,
        name: 'id',
        fHidden: true,
      },
      {
        fnLabel: () => `${this.props.t('Logical Id')}`,
        name: 'logicalId',
        rawValue: o => o.get('logicalId'),
      },
      {
        fnLabel: () => `${this.props.t('Name')}`,
        name: 'name',
        rawValue: o => o.get('name'),
      },
    ];
  }

  _onEnableConditionEvent = conditionIndex => event => {
    this._onChangeContextConditionEntities(conditionIndex, 'includeAll')(event.target.checked);
  };

  _onChangeContextConditionEntities = (conditionIndex, prop) => value => {
    const entityId = this.state.selectedEntity.get('logicalId');
    const contextId = this.state.selectedContext.getIn(['logicalId']);
    const subcontext = this.state.selectedSubcontext;
    const actionId = this.state.selectedAction.get('logicalId');
    let acl = this._getAclSet(entityId, contextId, subcontext.getIn(['logicalId']), actionId);
    if (!acl.getIn(['contextData', 'conditions'])) {
      if (!acl.getIn(['contextData'])) {
        acl = acl.setIn(['contextData'], Immutable.Map());
      }
      acl = acl.setIn(['contextData', 'conditions'], subcontext.getIn(['data', 'conditions']));
    }
    acl = acl.setIn(['contextData', 'conditions', conditionIndex, prop], value);

    this._onChangeAcl(entityId, contextId, subcontext.getIn(['logicalId']), actionId)(acl);
  };

  _isAclAlive(entity, context, subcontext, action) {
    const checkAclAlive = (acls, entity, context, subcontext, action) => {
      return !!(
        this.props.values &&
        this.props.values[acls] &&
        this.props.values[acls].find(
          acl =>
            acl.get('isAlive') &&
            acl.get('entityId') === entity &&
            (context ? acl.get('contextId') === context : true) &&
            (subcontext ? acl.get('subcontextId') === subcontext : true) &&
            (action ? acl.get('actionId') === action : true)
        )
      );
    };

    return (
      checkAclAlive('aclPrivileges', entity, context, subcontext, action)
    );
  }

  _getAclSet(entity, context, subcontext, action) {
    let acl =
      (this._curValues.aclPrivileges &&
        this._curValues.aclPrivileges.find(
          aclP =>
            aclP.get('entityId') === entity &&
            aclP.get('contextId') === context &&
            aclP.get('subcontextId') === subcontext &&
            aclP.get('actionId') === action
        )) ||
      (this._curValues.aclPrivileges &&
        this._curValues.aclPrivileges.find(
          aclP =>
            aclP.get('entityId') === entity &&
            aclP.get('contextId') === context &&
            aclP.get('subcontextId') === subcontext &&
            aclP.get('actionId') === action
        ));

    return acl;
  }

  _onChangeAcl = (entity, context, subcontext, action) => acl => {
    let { aclPrivileges } = this.props.values;

    if (!aclPrivileges) {
      aclPrivileges = new Immutable.List();
    }

    const aclIndexNew = aclPrivileges.findIndex(
      aclP =>
        aclP.get('entityId') === entity &&
        aclP.get('contextId') === context &&
        aclP.get('subcontextId') === subcontext &&
        aclP.get('actionId') === action
    );

    if (aclIndexNew > -1) {
      aclPrivileges = aclPrivileges.setIn([aclIndexNew], acl);
    } else {
      aclPrivileges = aclPrivileges.push(acl);
    }

    this.props.onChange('aclPrivileges', aclPrivileges);
  };

  _onChangeAclValue = (entity, context, subcontext, action) => (prop, value) => {
    let acl = this._getAclSet(entity, context, subcontext, action);

    if (!acl) {
      // If no acl, create default acl for entity
      acl = Immutable.Map(
        Immutable.fromJS({
          securityRoleId: this.props.values.logicalId,
          entityId: entity,
          contextId: context,
          subcontextId: subcontext,
          actionId: action,
          isAlive: true,
          data: { includeAll: true },
        })
      );
    } else {
      //Apply changes to acl
      if (prop === 'enabled') {
        acl = acl.set('isAlive', value);
      } else {
        acl = acl.setIn(['data', prop], value);
      }
    }

    this._onChangeAcl(entity, context, subcontext, action)(acl);
  };

  componentDidMount() {
    this.needsManager.fetchNeeds();
  }

}

RolePrivileges.propTypes = {
  app: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  values: PropTypes.object.isRequired,
  onChange: PropTypes.func.isRequired,
};

export default withRouter(withStyles(styles)(WithAllPropsApp(withTranslation()(RolePrivileges))));
